/**
 * Client de l'API d'administration.
 *
 * Le jeton provient de Supabase Auth : il prouve QUI est l'humain. Les données,
 * elles, sont lues par le worker avec le rôle `app_admin`. Le navigateur ne
 * parle jamais à PostgreSQL — il n'existe qu'une seule voie d'accès.
 */

const BASE_URL = import.meta.env['VITE_ADMIN_API_URL'] ?? 'http://127.0.0.1:8789';

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function hasAccessToken(): boolean {
  return accessToken !== null && accessToken !== '';
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(accessToken === null ? {} : { authorization: `Bearer ${accessToken}` }),
      ...init.headers,
    },
  });

  if (response.status === 401) throw new Error('Session expirée ou jeton invalide.');
  if (response.status === 503) throw new Error('Service indisponible — vérifier la configuration.');
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Erreur ${String(response.status)}`);
  }
  return (await response.json()) as T;
}

export interface Movement {
  movement_id: string;
  movement_reference: string;
  port_call_reference: string | null;
  tractor_reference: string | null;
  trailer_reference: string | null;
  status: string;
  identification_status: string;
  identified_by_qr: boolean;
  identified_by_external_source: boolean;
  control_started_at: string | null;
  deposited_at: string | null;
  // La POSITION, et pas seulement la place : aucune place n'est numérotée au
  // sol à Bastia. L'API la rendait déjà, l'écran ne la déclarait pas.
  deposit_latitude: number | null;
  deposit_longitude: number | null;
  deposit_accuracy_m: number | null;
  deposit_position_reliable: boolean | null;
  parking_slot_code: string | null;
  parking_area_code: string | null;
  confidence_score: number | null;
  requires_manual_review: boolean;
  reason_codes: string[];
  computed_at: string;
  is_simulated: boolean;
}

export interface Anomaly {
  anomaly_id: string;
  anomaly_code: string;
  severity: string;
  status: string;
  movement_reference: string | null;
  trailer_reference: string | null;
  tractor_reference: string | null;
  detected_at: string;
  detected_by: string;
  details: Record<string, unknown>;
}

export interface TimelineEntry {
  at: string;
  kind: string;
  label: string;
  eventId: string | null;
  details: Record<string, unknown>;
}

export interface Health {
  queues: {
    deadLettersUnresolved: number;
    lastObservationAt: string | null;
    observationsLastHour: number;
    devicesSeenLastHour: number;
    lateObservationsLastHour: number;
  };
  device_clock_drift: {
    deviceId: string;
    clockSkewMs: number | null;
    clockSkewAt: string | null;
    lastSeenAt: string | null;
  }[];
}

interface Paged<T> {
  data: T[];
  pagination: { next_cursor: string | null; has_more: boolean };
}

export async function listMovements(params: Record<string, string> = {}): Promise<Paged<Movement>> {
  const query = new URLSearchParams(params).toString();
  return request<Paged<Movement>>(`/admin/v1/movements${query === '' ? '' : `?${query}`}`);
}

export async function getTimeline(movementId: string): Promise<{ data: TimelineEntry[] }> {
  return request<{ data: TimelineEntry[] }>(`/admin/v1/movements/${movementId}/timeline`);
}

export async function listAnomalies(params: Record<string, string> = {}): Promise<Paged<Anomaly>> {
  const query = new URLSearchParams(params).toString();
  return request<Paged<Anomaly>>(`/admin/v1/anomalies${query === '' ? '' : `?${query}`}`);
}

export async function resolveAnomaly(
  anomalyId: string,
  status: 'acknowledged' | 'resolved' | 'dismissed',
  note: string,
): Promise<void> {
  await request(`/admin/v1/anomalies/${anomalyId}`, {
    method: 'POST',
    body: JSON.stringify({ status, ...(note === '' ? {} : { note }) }),
  });
}

export interface CorrectionInput {
  correction_type: string;
  movement_id?: string;
  cycle_id?: string;
  trailer_id?: string;
  anomaly_id?: string;
  previous_value?: unknown;
  new_value: unknown;
  reason: string;
}

export async function submitCorrection(input: CorrectionInput): Promise<{ correction_id: string }> {
  return request<{ correction_id: string }>('/admin/v1/corrections', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Une remorque ANNONCÉE par le manifeste, avec son heure de disponibilité.
 *
 * C'est le « quand » livré avant l'accostage — la seule ressource utile au
 * transporteur AVANT le débarquement. `source` dit d'où vient l'heure, et il
 * doit être affiché : présenter une estimation comme un constat ferait venir
 * des chauffeurs pour rien.
 */
export interface ExpectedTrailer {
  trailer_id: string;
  trailer_reference: string;
  predicted_discharge_rank: number;
  available_at: string;
  source: 'constate' | 'cadence_mesuree' | 'cadence_reference';
  deposited_at: string | null;
  position: {
    latitude: number;
    longitude: number;
    accuracy_m: number | null;
    reliable: boolean;
  } | null;
  parking_area_code: string | null;
  parking_slot_code: string | null;
  identification_status: string | null;
  is_overdue: boolean;
}

export interface PortCall {
  reference: string;
  ship_name: string | null;
  status: string;
  movement_count: number;
}

export async function listPortCalls(): Promise<{ data: PortCall[] }> {
  return request<{ data: PortCall[] }>('/admin/v1/port-calls');
}

export async function listExpected(reference: string): Promise<{ data: ExpectedTrailer[] }> {
  return request<{ data: ExpectedTrailer[] }>(
    `/admin/v1/port-calls/${encodeURIComponent(reference)}/expected`,
  );
}

export async function getHealth(): Promise<Health> {
  return request<Health>('/admin/v1/health');
}
